import { Checkbox } from '@/global/components/Checkbox'
import Collapse from '@/global/components/Collapse'
import Grid from '@/global/components/ui/Grid'
import ContentGuide from '@/global/guides/ContentGuide'
import { cn } from '@/lib/utils'
import { formatPorcentagemRegra } from '@acme/common'
import { SetAlwaysRequired } from '@acme/core'
import {
    CardRadioForm,
    FormField,
    FormFieldCheckbox,
    FormFieldSelect,
    FormFieldTime,
} from 'global/components/Form'
import IconeAjuda from 'global/components/IconeAjuda'
import { CornerDownRightIcon } from 'lucide-react'
import React, { useCallback, useEffect } from 'react'
import { useFormContext } from 'react-hook-form'
import { PorcentagemRegraCalculo } from 'service/api'
import { match } from 'ts-pattern'
import VerticalDivider from '../../../../../global/components/Divider/VerticalDivider'
import { horasExtrasModel, IHorasExtras } from '../../constants'

type TipoHorasExcedentes = 'DIARIO' | 'SEMANAL' | 'MENSAL'
const DIAS_UTEIS = ['seg', 'ter', 'qua', 'qui', 'sex']

type Porcentagens = SetAlwaysRequired<PorcentagemRegraCalculo, 'porcentagem'>[]

const opcoesPorcentagem = (porcentagens: Porcentagens, disabled?: boolean) =>
    porcentagens.map(item => ({
        value: item.porcentagem,
        label: formatPorcentagemRegra(item.porcentagem),
        disabled,
    }))

const OPCAO_REGRA_DO_DIA = { value: null, label: 'Regra do dia' }

const OPCOES_TIPO_HORAS_EXCEDENTES = [
    {
        value: 'DIARIO',
        titulo: 'Diário',
        descricao:
            'As primeiras horas extras contam a cada dia, com faixas próprias por dia da semana.',
    },
    {
        value: 'SEMANAL',
        titulo: 'Semanal',
        descricao:
            'As primeiras horas extras acumulam na semana; o que passar delas vai para as demais.',
    },
    {
        value: 'MENSAL',
        titulo: 'Mensal',
        descricao:
            'As primeiras horas extras acumulam no mês; o que passar delas vai para as demais.',
    },
]

const OPCOES_PRIORIDADE = [
    { value: 'maior', label: 'Maior' },
    { value: 'menor', label: 'Menor' },
]

function Card({
    children,
    className,
}: {
    children: React.ReactNode
    className?: string
}) {
    return (
        <ContentGuide.Card
            className={cn(
                'h-fit flex-row max-w-full gap-2 rounded-sm',
                className,
            )}
        >
            {children}
        </ContentGuide.Card>
    )
}

/**
 * Distribui os campos de uma faixa de horas extras: empilhados no celular,
 * em duas linhas a partir de `md` e em uma linha só a partir de `lg`.
 * Sem `todas`, o rótulo ocupa também a coluna dela.
 */
function LinhaLayout({
    rotulo,
    todas,
    primeiras,
    demais,
    prioridade,
    children,
}: {
    rotulo: React.ReactNode
    todas?: React.ReactNode
    primeiras: React.ReactNode
    demais: React.ReactNode
    prioridade: React.ReactNode
    children?: React.ReactNode
}) {
    return (
        <Grid container columns={30} className="items-center">
            <Grid item xs={todas ? 24 : 30} lg={todas ? 5 : 8}>
                {rotulo}
            </Grid>
            {todas && (
                <Grid item xs={6} lg={3}>
                    {todas}
                </Grid>
            )}
            <Grid item xs={30} md={14} lg={10}>
                <Card>{primeiras}</Card>
            </Grid>
            <Grid item xs={10} md={8} lg={7}>
                <Card>{demais}</Card>
            </Grid>
            <Grid item xs={20} md={8} lg={5}>
                <Card>{prioridade}</Card>
            </Grid>
            {/* Filho solto viraria uma trilha de 1/30 da linha — a caixa de
                seleção do rodapé precisa da linha inteira. */}
            {children && (
                <Grid item xs={30}>
                    {children}
                </Grid>
            )}
        </Grid>
    )
}

function Rotulo({ children }: { children: React.ReactNode }) {
    return <span className="text-nowrap text-sm font-semibold">{children}</span>
}

/** Agrupa a faixa de um dia com as caixas de seleção que se referem a ela. */
function Bloco({ children }: { children: React.ReactNode }) {
    return (
        <div className="flex flex-col gap-1 rounded-lg border border-border bg-muted/30 p-3">
            {children}
        </div>
    )
}

/** Faixa única de horas extras das regras semanal e mensal. */
function LinhaPeriodo({
    periodo,
    porcentagens,
}: {
    periodo: 'semanal' | 'mensal'
    porcentagens: Porcentagens
}) {
    const { getValues, setValue } = useFormContext()
    const campoPrimeiras = `horasExtras.${periodo}.qtdPrimeirasHrs`

    return (
        <LinhaLayout
            rotulo={
                <Rotulo>{periodo === 'semanal' ? 'Semana' : 'Mensal'}</Rotulo>
            }
            primeiras={
                <>
                    <FormField
                        name={campoPrimeiras}
                        label="Primeiras"
                        required
                        options={{
                            numericOnly: true,
                            blocks: [2, 2],
                            delimiter: ':',
                        }}
                        unHelperText
                        onBlur={() => {
                            setValue(
                                campoPrimeiras,
                                getValues<string>(campoPrimeiras).padEnd(
                                    5,
                                    '0',
                                ),
                            )
                        }}
                        endAdornment={
                            <span className="mr-2 text-muted-foreground">
                                h
                            </span>
                        }
                    />
                    <FormFieldSelect
                        required
                        name={`horasExtras.${periodo}.porcentagemPrimeirasHrs`}
                        label="Será"
                        unHelperText
                        options={opcoesPorcentagem(porcentagens)}
                    />
                </>
            }
            demais={
                <FormFieldSelect
                    required
                    name={`horasExtras.${periodo}.porcentagemDemaisHrs`}
                    label="Demais"
                    unHelperText
                    options={opcoesPorcentagem(porcentagens)}
                />
            }
            prioridade={
                <FormFieldSelect
                    name={`horasExtras.${periodo}.prioridadeCompensacao`}
                    required
                    label="Prioridade"
                    unHelperText
                    options={OPCOES_PRIORIDADE}
                />
            }
        />
    )
}

/** Domingo nas regras semanal e mensal, que tem faixa própria. */
function LinhaDomingo({ porcentagens }: { porcentagens: Porcentagens }) {
    const { watch } = useFormContext()
    const isTodas = watch('horasExtras[dom].todas', false)

    return (
        <LinhaLayout
            rotulo={<Rotulo>Domingo</Rotulo>}
            primeiras={
                <>
                    <FormFieldTime
                        name="horasExtras[dom].qtdPrimeirasHrs"
                        label="Primeiras"
                        disabled={isTodas}
                        required
                        unHelperText
                        endAdornment={
                            <span className="mr-2 text-muted-foreground">
                                h
                            </span>
                        }
                    />
                    <FormFieldSelect
                        required
                        name="horasExtras[dom].porcentagemPrimeirasHrs"
                        label="Será"
                        unHelperText
                        options={opcoesPorcentagem(porcentagens)}
                    />
                </>
            }
            demais={
                <FormFieldSelect
                    required
                    name="horasExtras[dom].porcentagemDemaisHrs"
                    disabled={isTodas}
                    label="Demais"
                    unHelperText
                    options={opcoesPorcentagem(porcentagens)}
                />
            }
            prioridade={
                <FormFieldSelect
                    name="horasExtras[dom].prioridadeCompensacao"
                    required
                    label="Prioridade"
                    unHelperText
                    options={OPCOES_PRIORIDADE}
                />
            }
        />
    )
}

export default function HorasExtras() {
    const { watch, getValues, setValue } = useFormContext()

    const [horasExtrasFeriado, pForm = [], configFeriado] = watch([
        'horasExtrasFeriado',
        'porcentagens',
        'horasExtras.fer',
    ]) as [boolean, PorcentagemRegraCalculo[], IHorasExtras]

    const porcentagens: SetAlwaysRequired<
        PorcentagemRegraCalculo,
        'porcentagem'
    >[] = pForm.filter(it => !!it.porcentagem) as any

    const tipoHorasExcedentes: TipoHorasExcedentes = watch(
        'tipoHorasExcedentes',
        'DIARIO',
    )

    useEffect(() => {
        if (!horasExtrasFeriado) {
            setValue('horasExtras.fer', {
                ...configFeriado,
                porcentagemDemaisHrs: null,
                porcentagemPrimeirasHrs: null,
            })
        }
    }, [porcentagens.map(p => p.porcentagem).join(', ')])

    const [editarSemana, setEditarSemana] = React.useState(() => {
        const semExists = Object.keys(getValues('horasExtras') ?? {}).includes(
            'sem',
        )

        return !semExists
    })

    const handleChange = (checked: boolean) => {
        setEditarSemana(checked)
    }

    return (
        <>
            <CardRadioForm
                name="tipoHorasExcedentes"
                label="Configuração das horas estendidas"
                options={OPCOES_TIPO_HORAS_EXCEDENTES}
            />
            <div className="h-4" />
            <div className="flex flex-col gap-3">
                {match(tipoHorasExcedentes)
                    .with('DIARIO', () => (
                        <RenderHorasExtras
                            editarSemana={editarSemana}
                            porcentagem={porcentagens}
                            tipoHorasExcedentes={tipoHorasExcedentes}
                            handleChange={handleChange}
                        />
                    ))
                    .with('SEMANAL', 'MENSAL', periodo => (
                        // Remonta ao trocar de período: os campos mudam de nome.
                        <React.Fragment key={periodo}>
                            <Bloco>
                                <LinhaPeriodo
                                    periodo={
                                        periodo === 'SEMANAL'
                                            ? 'semanal'
                                            : 'mensal'
                                    }
                                    porcentagens={porcentagens}
                                />
                            </Bloco>
                            <Bloco>
                                <LinhaDomingo porcentagens={porcentagens} />
                            </Bloco>
                        </React.Fragment>
                    ))
                    .exhaustive()}
                <Bloco>
                    <FormFieldCheckbox
                        name="horasExtrasFeriado"
                        label="Horas extras no feriado"
                        className="ml-auto flex w-fit"
                        onChange={checked => {
                            if (checked)
                                setValue('horasExtras.fer', {
                                    ...configFeriado,
                                    porcentagemDemaisHrs: null,
                                    porcentagemPrimeirasHrs: null,
                                })
                        }}
                        endAdornment={
                            <IconeAjuda>
                                Horas extras no feriado habilitadas por padrão.
                                Desabilite se não aplicável.
                            </IconeAjuda>
                        }
                    />
                    <Linha
                        disabled={!horasExtrasFeriado}
                        item={horasExtrasModel.horasExtras.fer}
                        tipoHorasExcedentes={tipoHorasExcedentes}
                        editar={true}
                        key={
                            horasExtrasFeriado
                                ? porcentagens.map(p => p.id).join(', ')
                                : 'fer'
                        }
                        index={'fer'}
                        porcentagem={porcentagens}
                    >
                        <div className="w-full flex justify-end">
                            <CheckboxControlePorTurno dia="fer" />
                        </div>
                    </Linha>
                </Bloco>
            </div>
            <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormFieldSelect
                    name="folgaPorcentagem"
                    label="Percentual em dia de folga"
                    defaultValue={null}
                    options={[
                        OPCAO_REGRA_DO_DIA,
                        ...opcoesPorcentagem(porcentagens),
                    ]}
                    endAdornment={
                        <IconeAjuda>
                            Aplicado a todas as horas trabalhadas em dia com
                            ocorrência de folga lançada (ex.: DSR). Em "Regra do
                            dia", vale a regra do dia ou do feriado.
                        </IconeAjuda>
                    }
                />
                <FormFieldSelect
                    name="afastamentoPorcentagem"
                    label="Percentual em dia de afastamento"
                    defaultValue={null}
                    options={[
                        OPCAO_REGRA_DO_DIA,
                        ...opcoesPorcentagem(porcentagens),
                    ]}
                    endAdornment={
                        <IconeAjuda>
                            Aplicado a todas as horas trabalhadas em dia com
                            ocorrência de afastamento lançada (ex.: férias,
                            atestado). Em "Regra do dia", vale a regra do dia ou
                            do feriado.
                        </IconeAjuda>
                    }
                />
            </div>
        </>
    )
}

function RenderHorasExtras({
    editarSemana,
    handleChange,
    porcentagem,
    tipoHorasExcedentes,
}: {
    editarSemana: boolean
    handleChange: (checked: boolean) => void
    porcentagem: SetAlwaysRequired<PorcentagemRegraCalculo, 'porcentagem'>[]
    tipoHorasExcedentes: TipoHorasExcedentes
}) {
    return (
        <>
            {Object.entries(horasExtrasModel.horasExtras).map(
                ([key, item], index) =>
                    ['sem', 'sab', 'dom'].includes(key) && (
                        <Bloco key={key}>
                            <Linha
                                editar={index === 0 ? !editarSemana : true}
                                item={item}
                                index={key}
                                porcentagem={porcentagem}
                                tipoHorasExcedentes={tipoHorasExcedentes}
                            >
                                {key === 'sem' ? (
                                    <SemanaComponent
                                        editarSemana={editarSemana}
                                        handleChange={handleChange}
                                        porcentagem={porcentagem}
                                        tipoHorasExcedentes={
                                            tipoHorasExcedentes
                                        }
                                    />
                                ) : (
                                    <div className="w-full flex justify-end">
                                        <CheckboxControlePorTurno dia={key} />
                                    </div>
                                )}
                            </Linha>
                        </Bloco>
                    ),
            )}
        </>
    )
}

function SemanaComponent({
    editarSemana,
    handleChange,
    porcentagem,
    tipoHorasExcedentes,
}: {
    editarSemana: boolean
    handleChange: (checked: boolean) => void
    porcentagem: SetAlwaysRequired<PorcentagemRegraCalculo, 'porcentagem'>[]
    tipoHorasExcedentes: TipoHorasExcedentes
}) {
    return (
        <>
            <div className="flex w-full justify-end items-center">
                <CheckboxControlePorTurno dia="sem" disabled={editarSemana} />
                <VerticalDivider className="ml-2 mr-6" />
                <Checkbox
                    id="editarSemana"
                    checked={editarSemana}
                    onCheckedChange={valor => handleChange(valor === true)}
                    label={
                        <>
                            Editar semana
                            <IconeAjuda className="ml-2 -mr-3">
                                <div className="w-52">
                                    Habilite para editar os dias de segunda a
                                    sexta-feira separadamente.
                                </div>
                            </IconeAjuda>
                        </>
                    }
                />
            </div>
            <Collapse open={editarSemana} className="w-full" keepMounted>
                <div
                    className={cn(
                        'flex flex-col gap-3 pt-3 w-full',
                        'transition-opacity duration-300',
                        {
                            'opacity-0': !editarSemana,
                        },
                    )}
                >
                    {Object.entries(horasExtrasModel.horasExtras).map(
                        ([subKey, subItem]) =>
                            DIAS_UTEIS.includes(subKey) && (
                                <div
                                    key={subKey}
                                    className="border-t border-border pt-3"
                                >
                                    <Linha
                                        editar={editarSemana}
                                        item={subItem}
                                        index={subKey}
                                        porcentagem={porcentagem}
                                        tipoHorasExcedentes={
                                            tipoHorasExcedentes
                                        }
                                        isChildren
                                    >
                                        <div className="w-full flex justify-end">
                                            <CheckboxControlePorTurno
                                                dia={subKey}
                                            />
                                        </div>
                                    </Linha>
                                </div>
                            ),
                    )}
                </div>
            </Collapse>
        </>
    )
}

interface LinhaProps {
    item: IHorasExtras
    index: string
    editar: boolean
    onChange?: (name: string, value: unknown) => void
    porcentagem: SetAlwaysRequired<PorcentagemRegraCalculo, 'porcentagem'>[]
    disabled?: boolean
    children?: React.ReactNode
    tipoHorasExcedentes: TipoHorasExcedentes
    isChildren?: boolean
}

const Linha: React.FC<LinhaProps> = ({
    item,
    index,
    editar,
    porcentagem,
    children,
    disabled = false,
    tipoHorasExcedentes,
    isChildren,
}) => {
    const { watch, setValue } = useFormContext()

    const isTodas = useCallback(
        (key: string) => {
            return watch(`horasExtras[${key}].todas`, false)
        },
        [watch],
    )

    const isTurno = useCallback(
        (index: string) => {
            return watch(`horasExtras[${index}].controleTurno`, false)
        },
        [watch],
    )

    function handleChangeSemana(field: string, value: any) {
        if (index === 'sem') {
            DIAS_UTEIS.forEach(dia => {
                setValue(`horasExtras[${dia}].${field}`, value)
            })
        }
    }

    const isFeriadoNaoDiario =
        index === 'fer' && tipoHorasExcedentes !== 'DIARIO'

    return (
        <LinhaLayout
            rotulo={
                <span
                    className={cn(
                        'text-nowrap inline-flex gap-1',
                        'items-start text-sm font-semibold',
                        { 'line-through': !editar },
                    )}
                >
                    {isChildren && <CornerDownRightIcon className="size-4" />}
                    {item.nome}
                </span>
            }
            todas={
                isFeriadoNaoDiario ? (
                    <div className="w-full" />
                ) : (
                    <FormFieldCheckbox
                        name={`horasExtras[${index}].todas`}
                        label="Todas"
                        disabled={!editar || disabled}
                        onChange={marcado => {
                            handleChangeSemana('todas', marcado)
                            if (marcado) {
                                setValue(
                                    `horasExtras[${index}].porcentagemDemaisHrs`,
                                    null,
                                )
                                setValue(
                                    `horasExtras[${index}].qtdPrimeirasHrs`,
                                    '',
                                )
                                handleChangeSemana('porcentagemDemaisHrs', null)
                                handleChangeSemana('qtdPrimeirasHrs', '')

                                setValue(
                                    `horasExtras[${index}].controleTurno`,
                                    false,
                                )
                                handleChangeSemana('controleTurno', false)
                            }
                        }}
                    />
                )
            }
            primeiras={
                <>
                    <FormFieldTime
                        disabled={
                            !editar ||
                            isTodas(index) ||
                            disabled ||
                            isTurno(index)
                        }
                        name={`horasExtras[${index}].qtdPrimeirasHrs`}
                        label={isTurno(index) ? 'Primeiro turno' : 'Primeiras'}
                        required={!isTodas(index) && !isTurno(index)}
                        unHelperText
                        endAdornment={
                            <span className="mr-2 text-muted-foreground">
                                h
                            </span>
                        }
                        onChange={hora => {
                            handleChangeSemana('qtdPrimeirasHrs', hora)
                        }}
                    />
                    <FormFieldSelect
                        required
                        name={`horasExtras[${index}].porcentagemPrimeirasHrs`}
                        label="Será"
                        unHelperText
                        disabled={!editar || disabled}
                        options={opcoesPorcentagem(porcentagem, disabled)}
                        onChange={valor => {
                            handleChangeSemana('porcentagemPrimeirasHrs', valor)
                        }}
                    />
                </>
            }
            demais={
                <FormFieldSelect
                    disabled={!editar || isTodas(index) || disabled}
                    required={!isTodas(index)}
                    name={`horasExtras[${index}].porcentagemDemaisHrs`}
                    label={isTurno(index) ? 'Segundo turno' : 'Demais'}
                    unHelperText
                    options={opcoesPorcentagem(porcentagem)}
                    onChange={valor => {
                        handleChangeSemana('porcentagemDemaisHrs', valor)
                    }}
                />
            }
            prioridade={
                <FormFieldSelect
                    name={`horasExtras[${index}].prioridadeCompensacao`}
                    required
                    label="Prioridade"
                    unHelperText
                    disabled={!editar || disabled}
                    options={OPCOES_PRIORIDADE}
                    onChange={valor => {
                        handleChangeSemana('prioridadeCompensacao', valor)
                    }}
                />
            }
        >
            {children}
        </LinhaLayout>
    )
}

function CheckboxControlePorTurno({
    dia,
    disabled,
}: {
    dia: string
    disabled?: boolean
}) {
    const { watch, setValue } = useFormContext()
    const todas = watch(`horasExtras[${dia}].todas`, false)

    function handleChangeSemana(field: string, value: any) {
        if (dia === 'sem') {
            DIAS_UTEIS.forEach(dia => {
                setValue(`horasExtras[${dia}].${field}`, value)
            })
        }
    }

    return (
        <FormFieldCheckbox
            label="Controle por turno"
            name={`horasExtras[${dia}].controleTurno`}
            disabled={disabled || todas}
            onChange={marcado => {
                handleChangeSemana('controleTurno', marcado)
            }}
            endAdornment={
                <IconeAjuda className="-ml-2">
                    {todas && [
                        'Desabilite a opção "Todas" para alterar.',
                        <br />,
                    ]}
                    Se habilitado, o controle de horas extras será feito por
                    turno (manhã e tarde) ao invés de primeiras e demais horas.
                    <br />A divisão de turnos ocorre ao meio-dia.
                </IconeAjuda>
            }
        />
    )
}
